import { useState, useRef, useCallback, useEffect } from 'react';
import { Sidebar } from '@/components/layout/Sidebar';
import { DataTablePage } from '@/pages/DataTablePage';
import { InstanceManagementPage } from '@/pages/InstanceManagementPage';
import { TitleOptimizationPage } from '@/pages/TitleOptimizationPage';
import { ImageEditConfigPage } from '@/pages/ImageEditConfigPage';
import { LowQualityFilterPage } from '@/pages/LowQualityFilterPage';
import { SizeChartPage } from '@/pages/SizeChartPage';
import { SkuConfigPage } from '@/pages/SkuConfigPage';
import { LogPage } from '@/pages/LogPage';
import { BatchProgressBar } from '@/components/BatchProgressBar';
import { useDataStore } from '@/store/useDataStore';
import { importExcel } from '@/services/excelService';
import { FileSpreadsheet, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { initializeCollectionBoxBridge, useSourceStore } from '@/store/useSourceStore';

export default function App() {
  const [currentPage, setCurrentPage] = useState('table');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { hasData } = useDataStore();
  const importData = useDataStore((state) => state.importData);
  const sourceMode = useSourceStore((state) => state.mode);

  useEffect(() => initializeCollectionBoxBridge(importData), [importData]);

  useEffect(() => {
    if (sourceMode !== 'collection-box' || window.parent === window) return;
    const parentOrigin = new URLSearchParams(window.location.search).get('parent_origin');
    if (!parentOrigin) return;
    window.parent.postMessage({
      source: 'tabbygrid',
      type: 'collection-box:ui-state',
      page: currentPage,
    }, parentOrigin);
  }, [currentPage, sourceMode]);

  const handleNavigate = (page: string) => {
    setCurrentPage(page);
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'table': return <DataTablePage />;
      case 'instances': return <InstanceManagementPage />;
      case 'title': return <TitleOptimizationPage />;
      case 'image-edit': return <ImageEditConfigPage />;
      case 'filter': return <LowQualityFilterPage />;
      case 'size': return <SizeChartPage />;
      case 'sku': return <SkuConfigPage />;
      case 'log': return <LogPage />;
      default: return <DataTablePage />;
    }
  };

  const configPages = ['instances', 'image-edit', 'title', 'filter', 'size', 'sku', 'log'];
  const showPage = currentPage === 'table' || hasData || configPages.includes(currentPage);

  return (
    <div className="flex h-screen flex-col bg-white">
      {/* 全局批量操作进度 - 固定在右下角，跨页面持久显示 */}
      <BatchProgressBar />
      <div className="flex flex-1 overflow-hidden">
        {sourceMode !== 'collection-box' && (
          <Sidebar currentPage={currentPage} onNavigate={handleNavigate} hasData={hasData} collapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(v => !v)} />
        )}
        <main className="flex-1 overflow-hidden">
          {sourceMode === 'collection-box' ? (
            <DataTablePage />
          ) : showPage ? renderPage() : <EmptyState />}
        </main>
      </div>
    </div>
  );
}

function EmptyState() {
    const { importData } = useDataStore();
    const sourceMode = useSourceStore((state) => state.mode);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [importError, setImportError] = useState('');

  const handleFileImport = useCallback(async (file: File) => {
    setImporting(true);
    setImportError('');
    try {
      const data = await importExcel(file);
      importData(data);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : '导入失败');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [importData]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && /\.(xlsx|xls)$/i.test(file.name)) {
      handleFileImport(file);
    }
  }, [handleFileImport]);

  return (
    <div
      className={cn(
        'flex h-full flex-col items-center justify-center gap-6 p-8 transition-all duration-200',
      )}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileImport(f); }}
      />
      {sourceMode === 'collection-box' ? (
        <>
          <Loader2 size={48} className="animate-spin text-[#159b96]" />
          <span className="text-base font-medium text-[#159b96]">正在读取公共采集箱…</span>
        </>
      ) : importing ? (
        <>
          <Loader2 size={48} className="animate-spin text-[#159b96]" />
          <span className="text-base font-medium text-[#159b96]">正在导入…</span>
        </>
      ) : (
        <div
          className={cn(
            'flex flex-col items-center justify-center gap-5 rounded-3xl border-2 border-dashed px-16 py-12 transition-all duration-200 cursor-pointer',
            dragOver ? 'border-[#159b96] bg-[#effafa]' : 'border-zinc-200 bg-zinc-50/30 hover:border-[#8dd4d1] hover:bg-[#f5fbfb]',
          )}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className={cn('flex h-20 w-20 items-center justify-center rounded-2xl transition-colors', dragOver ? 'bg-[#d9f3f1] text-[#159b96]' : 'bg-[#eff7f7] text-[#5eb9b5]')}>
            <FileSpreadsheet size={36} />
          </div>
          <div className="text-center">
            <h2 className="text-lg font-semibold text-zinc-700">拖拽 Excel 文件到此处，或点击选择文件</h2>
            <p className="mt-1 text-sm text-zinc-500">支持 .xlsx / .xls 格式</p>
          </div>
          {importError && (
            <p className="text-sm text-red-500">{importError}</p>
          )}
        </div>
      )}
    </div>
  );
}
